// imports
import { Controller, useFormContext } from 'react-hook-form';
import { FC, Fragment, useCallback, useEffect, useState } from 'react';
import { Autocomplete, Box, Chip, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import { KeyboardArrowDownRounded as KeyboardArrowDownRoundedIcon } from '@mui/icons-material';
//interfaces , constants , helper
import { renderProductTypes } from 'lib/helper';
import { ACTIVE_TEXT, INACTIVE_TEXT, HTTP_STATUS } from 'constants/index';
import { ProductTypeMultiSelectorProps, SelectType } from 'interfaces';
import { ProductTypesPayload, useFindAllProductTypeLazyQuery } from 'generated/graphql';

const ProductTypeMultiSelector: FC<ProductTypeMultiSelectorProps> = ({
  name,
  title,
  disabled,
  isRequired,
  defaultValue,
  handleChange,
  margin = 'dense',
}) => {
  const [options, setOptions] = useState<ProductTypesPayload['data']>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const updatedOptions = [...renderProductTypes(options ?? [])];
  const { control } = useFormContext();

  const [findAllProductTypes, { loading }] = useFindAllProductTypeLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { findAllProductType } = data;
      const { data: formulationTypes, response } = findAllProductType || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        setOptions(formulationTypes as ProductTypesPayload['data']);
      }
    },
    onError: () => {
      setOptions([]);
    },
  });

  const fetchAllProductFormulationType = useCallback(async () => {
    await findAllProductTypes({
      variables: {
        findAllProductTypeInput: {
          paginationOptions: { page: 1, limit: 10 },
          search: searchQuery,
        },
      },
    });
  }, [findAllProductTypes, searchQuery]);

  useEffect(() => {
    fetchAllProductFormulationType();
  }, [fetchAllProductFormulationType]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const { name, onBlur, onChange, value, ref } = field;
        const { error } = fieldState;
        const { message } = error || {};
        return (
          <Autocomplete
            ref={ref}
            multiple
            onBlur={onBlur}
            disableClearable
            disabled={disabled}
            options={updatedOptions}
            value={value as SelectType[]}
            onChange={(_, value) => {
              onChange(value);
              handleChange && handleChange(value);
            }}
            renderOption={(props, option) => {
              const { name, isActive, value: val, description } = option || {};
              return (
                <Box component="li" sx={{ '& > div': { width: '100%' } }} key={val} {...props}>
                  <Box display={'flex'} flexDirection={'column'}>
                    <Box display={'flex'} justifyContent={'space-between'}>
                      <Typography variant="caption">{name || '--'}</Typography>
                      {typeof isActive === 'boolean' && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={isActive ? ACTIVE_TEXT : INACTIVE_TEXT}
                          color={isActive ? 'success' : 'error'}
                        />
                      )}
                    </Box>
                    {description && (
                      <Typography color="primary" fontStyle={'italic'}>
                        {description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            }}
            popupIcon={<KeyboardArrowDownRoundedIcon />}
            getOptionLabel={(option) => option.name || ''}
            isOptionEqualToValue={({ value: option }, { value }) => value === option}
            renderInput={(params) => (
              <TextField
                {...params}
                name={name}
                margin={margin}
                variant="outlined"
                helperText={message}
                error={Boolean(error)}
                defaultValue={defaultValue}
                label={isRequired ? `${title} *` : title}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <Fragment>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </Fragment>
                  ),
                }}>
                {updatedOptions?.map((item) => {
                  const { name, value } = item;
                  return (
                    <MenuItem key={value} value={value}>
                      {name}
                    </MenuItem>
                  );
                })}
              </TextField>
            )}
          />
        );
      }}
    />
  );
};

export default ProductTypeMultiSelector;
